import { useEffect, useState } from 'react';
import type { StaffAlertRow } from '@bbbk/schema';
import { Avatar, Icon } from '../common';
import { api, ApiError } from '../../lib/api';
import { useFetchTrigger } from '../../lib/useFetchTrigger';
import { readAfter } from './pushClient';

// Patricia's read-only card on the Rota (shift-alerts-who-is-on, flow 1.3,
// decision roster-change-notify-info-tab §4). Every active helper, On or Off
// and nothing else (Freddi, 2026-10-17) — live teal, attention gold: colour
// says whether it needs her, the word says what it is. She reads it; she cannot change it,
// and nothing on it is a control. It sits on the Rota's filter row, left of
// the Staff filter, as a strip: the mark, the name, the headline, then one
// chip per helper at the filter control's own height — every element of the
// prototype's card kept, the shape fitted to the row (Freddi, 2026-10-17).

const STATE_LABEL: Record<StaffAlertRow['state'], string> = { on: 'On', off: 'Off' };
const STATE_TONE: Record<StaffAlertRow['state'], string> = { on: 'live', off: 'attention' };

/** `error` is a read that did not land; `'unavailable'` is the server saying the VAPID secrets are not set (review, PR #280). */
export function ShiftAlertsCard({ rows, error }: { rows: StaffAlertRow[] | null; error?: boolean | 'unavailable' }) {
  return (
    <section className="rota-alerts" aria-label="Shift alerts">
      <div className="rota-alerts-head">
        <span className="rota-alerts-ic">
          <Icon name="bell" size={13} />
        </span>
        <span className="rota-alerts-title">Shift alerts</span>
        {(error || rows === null) && (
          <span className="rota-alerts-sub">{error === 'unavailable' ? 'Not set up yet' : error ? "Couldn't check" : 'Checking…'}</span>
        )}
      </div>
      {rows && rows.length > 0 && (
        <ul className="rota-alerts-list">
          {rows.map((r) => (
            <li key={r.id} className="rota-alerts-row" title={r.state === 'on' && r.since ? `On since ${new Date(r.since).toLocaleDateString('en-IE', { day: 'numeric', month: 'short' })}` : undefined}>
              <Avatar name={r.name} id={r.id} size={18} />
              <span className="rota-alerts-name">{r.name}</span>
              <span className={'alerts-pill ' + STATE_TONE[r.state]}>{STATE_LABEL[r.state]}</span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

// The live card: one read of GET /api/staff/alerts per Rota load, revalidated
// on the shell's own tick like every other read on the page.
export function RotaShiftAlerts() {
  const [rows, setRows] = useState<StaffAlertRow[] | null>(null);
  const [error, setError] = useState<boolean | 'unavailable'>(false);
  const trigger = useFetchTrigger();
  useEffect(() => {
    const run = trigger.begin();
    api<StaffAlertRow[]>('/staff/alerts', run.init)
      .then((r) => {
        if (!run.isCurrent()) return;
        run.markContent();
        setRows(r.data);
        setError(false);
      })
      .catch((e) => {
        if (!(e instanceof ApiError && e.status === 401) && run.isCurrent()) setError(readAfter(e) === 'unavailable' ? 'unavailable' : true);
      });
    return run.cancel;
  }, [trigger.nonce, trigger.tick]);
  return <ShiftAlertsCard rows={rows} error={error} />;
}
